import { Tooltip, useHover } from "@netapp/bxp-design-system-react";
import { StarIcon } from "@netapp/bxp-design-system-react/icons/monochrome";
import { FavoriteIcon } from "@netapp/bxp-style/react-icons/General";
import classNames from "classnames";
import { useCallback, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import usePermission from "utils/usePermission";
import { leftNav as i18nNamespace } from "../../../../../../i18n/namespace";
import { useFlyoutAnimation } from "../hooks/navigation/useFlyoutAnimation";
import leftNavStyles from "../CategoriesNav.module.scss";
import { MENU_ACTIONS } from "../constants/navigationConstants";
import { useCategoryMenuNavigation, useFlyoutAutoFocus, usePortalPositioning, useServiceCardNavigation } from "../hooks";
import { isVirtualTourEnabled } from "../../../../../../offline/utils";

const FavoritesIcon = ({ isHovered, isFavourited, isServiceActive = false, isFocused = false }) => {
    const shouldShowIcon = isHovered || isFocused;

    if (isServiceActive) {
        return isFavourited ? <StarIcon className={leftNavStyles['icon-favourite']} size={16} /> : (shouldShowIcon ? <FavoriteIcon className={leftNavStyles['icon-favourite-selected']} size={16} /> : null);
    }
    return isFavourited ? <StarIcon size={16} /> : (shouldShowIcon ? <FavoriteIcon size={16} /> : null);
}

const ServiceCard = ({ service, onCategoryMenuItemClicked, selectedService, index, onKeyDown, totalServices, favoriteButtonRef: _favouriteButtonRef }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(i18nNamespace);
    const favouritesByKey = useSelector(state => state.menu.favourites.byKey);
    const { permissions } = useSelector(state => state.tenancy);
    const isFavourited = favouritesByKey[service.key];

    const toggleFavourite = useCallback((service, isFavourited) => {
        dispatch({
            type: isFavourited ? MENU_ACTIONS.UNSET_FAVOURITE : MENU_ACTIONS.SET_FAVOURITE,
            payload: {
                serviceId: service.key
            }
        })
    }, [dispatch]);

    const hasPermission = usePermission();

    let isPermissionGranted = true;

    if (service?.permissionKeys) {
        const isPermissionKeys = service?.permissionKeys.map(
            (permissionKey) => service?.key === "NetApp.IdentityAccess" ? permissions.editAccount : hasPermission({
                permissionKey
            })
        );
        isPermissionGranted = isPermissionKeys.some(Boolean);
    }

    const toggleFavLabel = t("toggle_favorite");
    const { hoverParentProps, isHovered } = useHover();
    const isServiceActive = service?.name === selectedService?.name;
    let serviceDescriptionKey = service.key === "Platform.Canvas" ? "MyEstate_description" : (service.key.replace("NetApp.", "") + "_description");
    const internalFavoriteButtonRef = useRef();
    const favoriteButtonRef = _favouriteButtonRef || internalFavoriteButtonRef;
    const navLinkRef = useRef();
    const [isFavoriteButtonFocused, setIsFavoriteButtonFocused] = useState(false);
    const isAIDP = process.env.REACT_APP_IS_AIDP === "true";
    const isDarkSite = process.env.REACT_APP_IS_DARK === "true";
    const darkSiteOverrides = ['SupportCases', 'Credentials'];
    const serviceName = serviceDescriptionKey.replace('_description', '');

    if (isDarkSite && darkSiteOverrides.includes(serviceName)) {
        serviceDescriptionKey = serviceName + '_darksiteDescription';
    }

    const {
        handleFavoriteKeyDown,
        handleNavLinkKeyDown
    } = useServiceCardNavigation({
        index,
        totalServices,
        favoriteButtonRef,
        navLinkRef,
        onKeyDown,
        onToggleFavorite: () => toggleFavourite(service, isFavourited),
        onNavigate: onCategoryMenuItemClicked
    });

    const handleFavoriteInteraction = (e, shouldToggle = false) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.nativeEvent) {
            e.nativeEvent.stopImmediatePropagation();
        }
        if (shouldToggle) {
            toggleFavourite(service, isFavourited);
        }
    };

    return (
        <div
            key={service.key}
            data-service-card
            className={classNames(leftNavStyles['sub-tab-container'], { [leftNavStyles['sub-tab-has-permission']]: isPermissionGranted }, { [leftNavStyles['sub-tab-container-active']]: isServiceActive })}
            {...hoverParentProps}
        >
            {!isAIDP && <div className={leftNavStyles["favourite-button-container"]}>
                <button
                    ref={favoriteButtonRef}
                    style={{ marginTop: 4 }}
                    data-favorite-button
                    onClick={(e) => handleFavoriteInteraction(e, true)}
                    onMouseDown={handleFavoriteInteraction}
                    onKeyDown={handleFavoriteKeyDown}
                    onFocus={() => setIsFavoriteButtonFocused(true)}
                    onBlur={() => setIsFavoriteButtonFocused(false)}
                    aria-label={toggleFavLabel}
                    tabIndex={0}
                    disabled={!isPermissionGranted}
                >
                    {isPermissionGranted ? <FavoritesIcon isHovered={isHovered} isFavourited={isFavourited} isServiceActive={isServiceActive} isFocused={isFavoriteButtonFocused} /> : null}
                </button>
            </div>}
            {isPermissionGranted ? <NavLink
                ref={navLinkRef}
                to={service.to}
                data-nav={service.key}
                className={leftNavStyles['sub-tab']}
                activeClassName={leftNavStyles['sub-tab-active']}
                onClick={() => onCategoryMenuItemClicked()}
                onKeyDown={handleNavLinkKeyDown}
                aria-label={`${t(service.name)} ${t(serviceDescriptionKey)}`}
                tabIndex={0}
            >
                <div className={leftNavStyles["nav-link-item-container"]}>
                    {t(service.name)}
                    <span className={leftNavStyles['subtab-description']}>
                        {t(serviceDescriptionKey)}
                    </span>
                </div>
            </NavLink> : <div className={classNames(leftNavStyles['sub-tab'], leftNavStyles["nav-link-item-container"], leftNavStyles['sub-tab-disabled'])}>
                {t(service.name)}
                <span className={leftNavStyles['subtab-description']}>
                    {t(serviceDescriptionKey)}
                </span>
                <Tooltip>{isVirtualTourEnabled() ? t('netapp_cosole_simulator_not_supported') : t('user_do_not_have_access')}</Tooltip>
            </div>}
        </div>
    );
}

const CategoryServices = ({ services, onCategoryMenuItemClicked, selectedService, onKeyDown, firstFavoriteButtonRef }) => {
    return services.map((service, index) => (
        <ServiceCard
            key={service.key}
            service={service}
            onCategoryMenuItemClicked={onCategoryMenuItemClicked}
            selectedService={selectedService}
            index={index}
            onKeyDown={onKeyDown}
            totalServices={services.length}
            favoriteButtonRef={index === 0 ? firstFavoriteButtonRef : undefined}
        />
    ));
}

const CategoryFlyoutMenu = ({ services, onCategoryMenuItemClicked, selectedService, categoryRef, isOpen, shouldRefocus, isAnimationFinished }) => {
    const containerRef = useRef();
    const portalRef = useRef();
    const firstFavoriteButtonRef = useRef();
    const isAIDP = process.env.REACT_APP_IS_AIDP === "true";
    const portalPosition = {
        position: 'fixed',
        top: '72px',
        left: isAIDP ? '0px' : '228px',
        zIndex: isAIDP ? '2147483647' : '190000',
        height: 'calc(100vh - 72px)'
    };

    const { handleKeyDown } = useCategoryMenuNavigation(containerRef, categoryRef, null);
    const { showFlyout, flyoutAnimationClass, isFlyoutFullyOpen } = useFlyoutAnimation(isOpen, isAnimationFinished);

    useFlyoutAutoFocus(isFlyoutFullyOpen, shouldRefocus, firstFavoriteButtonRef, categoryRef);
    usePortalPositioning(portalRef, portalPosition, showFlyout);

    if (!showFlyout) {
        return null;
    }

    const menuContent = (
        <div
            ref={portalRef}
            data-flyout-menu
            className={flyoutAnimationClass}
        >
            <div ref={containerRef} className={leftNavStyles['sub-tabs-container']}>
                <CategoryServices
                    services={services}
                    onCategoryMenuItemClicked={onCategoryMenuItemClicked}
                    selectedService={selectedService}
                    onKeyDown={handleKeyDown}
                    firstFavoriteButtonRef={firstFavoriteButtonRef}
                />
            </div>
        </div>
    );

    return ReactDOM.createPortal(menuContent, document.getElementById('portal-root'));
};

export default CategoryFlyoutMenu;
